import React, { useState, useEffect } from 'react';
import styles from '../../style/styles/Form.module.css';
import { getFormConfig, validationFunctions } from '../../data/formConfig';

// Custom hook for form validation
export const useFormValidation = (formConfig) => {
  const [formData, setFormData] = useState({});
  const [validList, setValidList] = useState({});
  const [allValid, setAllValid] = useState(false);

  // Initialize form data and validation state
  useEffect(() => {
    const initialData = {};
    const initialValid = {};
    
    formConfig.fields.forEach(field => {
      initialData[field.id] = '';
      initialValid[field.id] = false;
    });

    // Initialize checkbox data if checkbox group is enabled
    if (formConfig.checkboxGroup.enabled) {
      const checkboxData = { all: false };
      const checkboxValid = { all: false };
      
      formConfig.checkboxGroup.checkboxes.forEach(checkbox => {
        checkboxData[checkbox.id] = false;
        checkboxValid[checkbox.id] = !checkbox.required; // Non-required checkboxes start as valid
      });
      
      Object.assign(initialData, checkboxData);
      Object.assign(initialValid, checkboxValid);
    }

    setFormData(initialData);
    setValidList(initialValid);
  }, [formConfig]);

  // Validate individual field
  const validateField = (fieldId, value, fieldConfig) => {
    const validation = fieldConfig.validation;
    if (!validation) return true;

    const validator = validationFunctions[validation.type];
    if (!validator) return true;

    return validator(value, validation);
  };

  // Handle input change with validation
  const handleInputChange = (fieldId, value, fieldConfig) => {
    let processedValue = value;

    // Process phone number input
    if (fieldConfig.type === 'tel' || fieldConfig.type === 'phone') {
      processedValue = value.replace(/[^0-9]/g, '');
      if (processedValue.length > fieldConfig.validation?.maxLength) {
        return; // Don't update if exceeds max length
      }
    }

    // Update form data
    setFormData(prev => ({
      ...prev,
      [fieldId]: processedValue
    }));

    // Validate field
    const isValid = validateField(fieldId, processedValue, fieldConfig);
    setValidList(prev => ({
      ...prev,
      [fieldId]: isValid
    }));
  };

  // Handle checkbox change
  const handleCheckboxChange = (checkboxId, checked) => {
    const newCheckboxData = {
      ...formData,
      [checkboxId]: checked
    };

    // Update 'all' checkbox if it's not the 'all' checkbox itself
    if (checkboxId !== 'all') {
      const allRequiredChecked = formConfig.checkboxGroup.checkboxes
        .filter(checkbox => checkbox.required)
        .every(checkbox => checkbox.id === checkboxId ? checked : formData[checkbox.id]);
      
      newCheckboxData.all = allRequiredChecked;
    } else {
      // If 'all' checkbox is clicked, update all other checkboxes
      formConfig.checkboxGroup.checkboxes.forEach(checkbox => {
        newCheckboxData[checkbox.id] = checked;
      });
    }

    setFormData(newCheckboxData);

    // Update validation state
    const newValidList = { ...validList };
    if (checkboxId !== 'all') {
      const checkbox = formConfig.checkboxGroup.checkboxes.find(cb => cb.id === checkboxId);
      newValidList[checkboxId] = checkbox.required ? checked : true;
      
      // Update 'all' checkbox validation
      const allRequiredChecked = formConfig.checkboxGroup.checkboxes
        .filter(checkbox => checkbox.required)
        .every(checkbox => checkbox.id === checkboxId ? checked : formData[checkbox.id]);
      newValidList.all = allRequiredChecked;
    } else {
      formConfig.checkboxGroup.checkboxes.forEach(checkbox => {
        newValidList[checkbox.id] = checkbox.required ? checked : true;
      });
      newValidList.all = checked;
    }

    setValidList(newValidList);
  };

  // Check if all fields are valid
  useEffect(() => {
    const fieldsValid = formConfig.fields.every(field => {
      if (!field.required) return true;
      return validList[field.id];
    });

    const checkboxesValid = formConfig.checkboxGroup.enabled 
      ? formConfig.checkboxGroup.checkboxes
          .filter(checkbox => checkbox.required)
          .every(checkbox => validList[checkbox.id])
      : true;

    setAllValid(fieldsValid && checkboxesValid);
  }, [validList, formConfig]);

  return {
    formData,
    validList,
    allValid,
    handleInputChange,
    handleCheckboxChange,
    setFormData
  };
};

// Form field component
export const FormField = ({ field, formData, validList, onInputChange, disabled }) => {
  const { id, type, label, placeholder, required, validation } = field;
  const value = formData[id] || '';
  const isValid = validList[id];
  const hasError = !isValid && value.length > 0;

  return (
    <div className={styles.CollectionForm__group}>
      <label className={`${styles.CollectionForm__label} ${required ? styles.CollectionForm__required : ''}`}>
        {label}
      </label>
      <input
        type={type}
        name={id}
        className={hasError ? styles.CollectionForm__input__warning : styles.CollectionForm__input}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onInputChange(id, e.target.value, field)}
        disabled={disabled}
        autoComplete="off"
      />
      {hasError && validation?.errorMessage && (
        <>
          <div className="spacing-6" />
          <div className={styles.CollectionForm__warning}>
            <img src="/img/units/cancel.png" alt="cancel" width={16} height={16} />
            <p className="h9 fc-warning">{validation.errorMessage}</p>
          </div>
        </>
      )}
    </div>
  );
};

// Checkbox group component
export const CheckboxGroup = ({ checkboxGroup, formData, validList, onCheckboxChange, disabled }) => {
  if (!checkboxGroup.enabled) return null;

  const { title, checkboxes } = checkboxGroup;

  return (
    <div className={styles.CollectionForm__checkboxGroup}>
      <label className={styles.CollectionForm__checkboxLabel}>
        <input
          type="checkbox"
          name="all"
          checked={formData.all || false}
          onChange={(e) => onCheckboxChange('all', e.target.checked)}
          disabled={disabled}
        />
        <span>{title}</span>
      </label>
      {checkboxes.map(checkbox => (
        <label key={checkbox.id} className={styles.CollectionForm__checkboxLabel}>
          <input
            type="checkbox"
            name={checkbox.id}
            checked={formData[checkbox.id] || false}
            onChange={(e) => onCheckboxChange(checkbox.id, e.target.checked)}
            disabled={disabled}
          />
          <div>
            {checkbox.label.split(checkbox.link?.text || '').map((part, index) => (
              <span key={index}>
                {part}
                {index < checkbox.label.split(checkbox.link?.text || '').length - 1 && checkbox.link && (
                  <span 
                    className={styles.CollectionForm__link}
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(checkbox.link.url, '_blank', 'noreferrer,noopener');
                    }}
                  >
                    {checkbox.link.text}
                  </span>
                )}
              </span>
            ))}
          </div>
        </label>
      ))}
    </div>
  );
};
